/**
 * Image Optimization Utilities
 * Herramientas para optimizar imágenes y mejorar Core Web Vitals
 */

import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { COLORS } from "../../styles/variables";

// Componente de imagen optimizada con lazy loading y WebP fallback
export const OptimizedImage = ({
  src,
  alt,
  width,
  height,
  className,
  priority = false,
  sizes = "100vw",
  quality = 80,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState("");
  const imgRef = useRef(null);

  // Generar URLs optimizadas
  const generateOptimizedSrc = (originalSrc, format = "webp") => {
    if (!originalSrc) return "";

    // Si es una URL local, intentar usar versión optimizada
    if (originalSrc.startsWith("/") || originalSrc.includes("localhost")) {
      const extension = originalSrc.split(".").pop();
      const basePath = originalSrc.replace(`.${extension}`, "");
      return `${basePath}.${format}`;
    }

    // Para URLs externas, retornar tal como está
    return originalSrc;
  };

  // WebP support detection
  const supportsWebP = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL("image/webp").indexOf("data:image/webp") === 0;
  };

  useEffect(() => {
    if (!src) return;

    // Determinar el mejor formato
    const useWebP = supportsWebP();
    const optimizedSrc = useWebP ? generateOptimizedSrc(src, "webp") : src;

    // Precargar imagen si es prioritaria
    if (priority) {
      const img = new Image();
      img.src = optimizedSrc;
      img.onload = () => setCurrentSrc(optimizedSrc);
      img.onerror = () => {
        // Fallback al formato original
        const fallbackImg = new Image();
        fallbackImg.src = src;
        fallbackImg.onload = () => setCurrentSrc(src);
        fallbackImg.onerror = () => setHasError(true);
      };
    } else {
      setCurrentSrc(optimizedSrc);
    }
  }, [src, priority]);

  // Intersection Observer para lazy loading
  useEffect(() => {
    if (priority || !imgRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLoaded) {
            const img = entry.target;
            img.src = currentSrc;
            observer.unobserve(img);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "50px", // Cargar 50px antes de que sea visible
      }
    );

    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [currentSrc, isLoaded, priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    // Disparar evento personalizado para analytics
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("image-loaded", {
          detail: { src: currentSrc, width, height },
        })
      );
    }
  };

  const handleError = () => {
    setHasError(true);
    // Intentar fallback al formato original
    if (currentSrc !== src) {
      setCurrentSrc(src);
      setHasError(false);
    }
  };

  if (hasError) {
    return (
      <ImagePlaceholder
        style={{ width: width || "100%", height: height || "auto" }}
        className={className}
      >
        <span>Error cargando imagen</span>
      </ImagePlaceholder>
    );
  }

  return (
    <ImageWrapper className={className}>
      <StyledImage
        ref={imgRef}
        src={priority ? currentSrc : undefined}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        $isLoaded={isLoaded}
        sizes={sizes}
        {...props}
      />
      {!isLoaded && (
        <ImageSkeleton
          style={{ width: width || "100%", height: height || "200px" }}
        />
      )}
    </ImageWrapper>
  );
};

// Styled components
const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const StyledImage = styled.img`
  width: 100%;
  height: auto;
  transition: opacity 0.3s ease;
  opacity: ${(props) => (props.$isLoaded ? 1 : 0)};

  ${(props) => (props.$isLoaded ? "" : "position: absolute; top: 0; left: 0;")}
`;

const ImageSkeleton = styled.div`
  background: linear-gradient(
    90deg,
    ${COLORS.LIGHT_GRAY}20 25%,
    ${COLORS.LIGHT_GRAY}40 50%,
    ${COLORS.LIGHT_GRAY}20 75%
  );
  background-size: 200% 100%;
  animation: loading 1.5s infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ImagePlaceholder = styled.div`
  background-color: ${COLORS.LIGHT_GRAY};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.DARK_GRAY};
  font-size: 14px;
`;

// Hook para precargar imágenes críticas
export const useImagePreloader = (imageSources = []) => {
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (imageSources.length === 0) {
      setIsLoading(false);
      return;
    }

    let loadedCount = 0;
    const totalImages = imageSources.length;

    const preloadImage = (src) => {
      const img = new Image();
      img.src = src;

      const handleLoad = () => {
        loadedCount++;
        setLoadedImages((prev) => new Set([...prev, src]));

        if (loadedCount === totalImages) {
          setIsLoading(false);
        }
      };

      img.onload = handleLoad;
      img.onerror = handleLoad; // Contar errores como completados
    };

    imageSources.forEach(preloadImage);
  }, [imageSources]);

  return { loadedImages, isLoading };
};

// Utilidad para convertir imágenes a WebP (cliente)
export const convertToWebP = (file, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      canvas.toBlob(resolve, "image/webp", quality);
    };

    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

export default OptimizedImage;
